import React, { useState } from "react";
import { base44 } from "@/api/base44Client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Search, Users as UsersIcon } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import ClientCard from "@/components/ui/apartments/clients/ClientCard";
import ClientForm from "@/components/ui/apartments/clients/ClientForm";
import { ExportMenu } from "@/components/shared/ExportMenu";
import { useDeleteWithUndo } from "@/components/shared/DeleteWithUndo";
import { extractId } from "@/utils/salesHelpers";

export default function ClientsPage() {
  const { canEdit } = useAuth();
  const [showForm, setShowForm] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const queryClient = useQueryClient();
  const { scheduleDelete } = useDeleteWithUndo();

  const { data: clients = [], isLoading } = useQuery({
    queryKey: ['clients'],
    queryFn: () => base44.entities.Client.list('-created_date'),
    initialData: [],
  });

  const { data: sales = [] } = useQuery({
    queryKey: ['sales'],
    queryFn: () => base44.entities.Sale.list(),
    initialData: [],
  });

  const isClientDefaulted = (clientId) => {
    return sales.some(s => extractId(s.client_id) === clientId && s.status === 'defaulted');
  };

  const createMutation = useMutation({
    mutationFn: (data) => base44.entities.Client.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      setShowForm(false);
      setEditingClient(null);
      if (window.addNotification) {
        window.addNotification('Client added successfully', 'success');
      }
    },
    onError: (error) => {
      if (window.addNotification) {
        window.addNotification(error?.message || 'Failed to create client', 'error');
      } else {
        console.error('Create client error:', error);
        alert(error?.message || 'Failed to create client. Check console for details.');
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.Client.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      setShowForm(false);
      setEditingClient(null);
      if (window.addNotification) {
        window.addNotification('Client updated successfully', 'success');
      }
    },
    onError: (error) => {
      if (window.addNotification) {
        window.addNotification(error?.message || 'Failed to update client', 'error');
      } else {
        console.error('Update client error:', error);
        alert(error?.message || 'Failed to update client. Check console for details.');
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (clientId) => base44.entities.Client.delete(clientId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['sales'] });
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      queryClient.invalidateQueries({ queryKey: ['receipts'] });
      queryClient.invalidateQueries({ queryKey: ['defaultedSales'] });
      queryClient.invalidateQueries({ queryKey: ['apartments'] });
    },
  });

  const handleSubmit = (data) => {
    if (editingClient) {
      updateMutation.mutate({ id: editingClient.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setShowForm(true);
  };

  const handleDelete = (client) => {
    scheduleDelete(
      client,
      "Client",
      (c) => deleteMutation.mutate(c.id),
      {
        restoreFn: async () => {
          await base44.entities.Client.restore(client.id);
          queryClient.invalidateQueries({ queryKey: ['clients'] });
          queryClient.invalidateQueries({ queryKey: ['sales'] });
          queryClient.invalidateQueries({ queryKey: ['payments'] });
          queryClient.invalidateQueries({ queryKey: ['receipts'] });
          queryClient.invalidateQueries({ queryKey: ['defaultedSales'] });
          queryClient.invalidateQueries({ queryKey: ['apartments'] });
        },
      }
    );
  };

  // Get client purchase count
  const getClientPurchaseCount = (clientId) => {
    return sales.filter(sale => extractId(sale.client_id) === clientId).length;
  };

  // Get client total spent
  const getClientTotalSpent = (clientId) => {
    return sales
      .filter(sale => extractId(sale.client_id) === clientId)
      .reduce((sum, sale) => sum + (sale.total_paid || 0), 0);
  };

  // Filter clients
  const filteredClients = clients.filter(client => {
    const matchesSearch =
      client.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      client.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      client.phone?.includes(searchQuery) ||
      client.identification_number?.includes(searchQuery);

    return matchesSearch;
  });

  const exportData = filteredClients.map(client => {
  const clientSales = sales.filter(s => extractId(s.client_id) === client.id);
  const contractNumbers = [...new Set(clientSales.map(s => s.contract_number).filter(Boolean))].join(', ');
  
  // Calculate Total Purchase Price (Sum of total_price from all sales for this client)
  const totalPurchasePrice = clientSales.reduce((sum, sale) => sum + (sale.total_price || 0), 0);

  return {
    full_name: client.full_name,
    contract_number: client.contract_number || contractNumbers || '',
    email: client.email || '',
    phone: client.phone,
    address: client.address || '',
    nationality: client.nationality || '',
    id_number: client.identification_number || '',
    notes: client.notes || '', // Added Notes column
    total_purchase_price: totalPurchasePrice, // Added Total Purchase Price column
  };
});

  const exportColumns = [
    { header: 'Full Name', key: 'full_name' },
    { header: 'Email', key: 'email' },
    { header: 'Phone', key: 'phone' },
    { header: 'Address', key: 'address' },
    { header: 'Nationality', key: 'nationality' },
    { header: 'ID Number', key: 'id_number' },
    { header: 'Contract Number', key: 'contract_number' },
    { header: 'Total Purchase Price', key: 'total_purchase_price' },
    { header: 'Notes', key: 'notes' }
  ];
  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-1">Clients</h1>
          <p className="text-sm text-slate-600">{clients.length} total clients</p>
        </div>
          {canEdit && (
            <div className="flex gap-3">
              <ExportMenu 
                data={exportData}
                columns={exportColumns}
                title="Clients List"
                filename={`clients_${new Date().toISOString().split('T')[0]}`}
              />
              <Button
                onClick={() => {
                  setEditingClient(null);
                  setShowForm(true);
                }}
                className="gap-2 bg-gradient-to-r from-slate-900 to-slate-700 hover:from-slate-800 hover:to-slate-600"
              >
                <Plus className="w-4 h-4" />
                Add Client
              </Button>
            </div>
          )}
        </div>

      {/* Filters */}
      <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            placeholder="Search by name, email, phone, or ID number..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

      {/* Clients List */}
        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto"></div>
          </div>
        ) : filteredClients.length === 0 ? (
          <div className="text-center py-12">
            <UsersIcon className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No clients found</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {filteredClients.map((client) => (
              <ClientCard
                key={client.id}
                client={client}
                isDefaulted={isClientDefaulted(client.id)}
                purchaseCount={getClientPurchaseCount(client.id)}
                totalSpent={getClientTotalSpent(client.id)}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}

        {/* Form Dialog */}
        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingClient ? 'Edit Client' : 'Add New Client'}
              </DialogTitle>
            </DialogHeader>
            <ClientForm
              client={editingClient}
              onSubmit={handleSubmit}
              onCancel={() => {
                setShowForm(false);
                setEditingClient(null);
              }}
              isLoading={createMutation.isPending || updateMutation.isPending}
            />
          </DialogContent>
        </Dialog>

    </div>
  );
}